import React, {
  useRef,
  Fragment,
  MutableRefObject,
  useEffect,
} from 'react';
import { View } from 'react-native';
import { styled, SxProp } from 'dripsy';
import { observer, useLocalObservable } from 'mobx-react-lite';
import Fuse from 'fuse.js';

import {
  ITextInputProps,
  TextInput,
  TextInputRefType,
} from '../../common/TextInput';
import { DropDownItem, DropDownItemOptionType } from './DropDownItem';
import { Touchable, TouchableTypeEnum } from '../../common/Touchable';
import { FlatList } from '../../common/FlatList';
import { Line } from '../../common/Line';
import { IS_WEB } from '@utils';
import { MeasureDimensionsType } from '@types';
import { useMeasureLayout, useVar } from '@hooks';

const DropDownContainer = styled(View)(
  (props: Pick<MeasureDimensionsType, 'top'>) => ({
    position: 'absolute',
    zIndex: 1,
    width: '100%',
    maxHeight: 240,
    top: props.top + 51 + 3,
    backgroundColor: '$background0',
    borderRadius: 8,
    variant: 'shadows.popover',
  }),
);

const DropDownItemContainer = styled(View)({
  zIndex: 2,
});

const listContentContainerStyle: SxProp = {
  px: '$6',
};

interface IPlaceholderInputContainerPropsStyle {
  isShowList: boolean;
}

const PlaceholderInputContainer = styled(View)(
  (props: IPlaceholderInputContainerPropsStyle) => ({
    position: 'absolute',
    width: '100%',
    zIndex: props.isShowList ? 1 : -1,
  }),
);

export interface IDropDownListProps
  extends Omit<
    ITextInputProps,
    'inputRef' | 'onFocus' | 'onBlur' | 'value' | 'onChangeText'
  > {
  containerRef: MutableRefObject<any>;
  options: DropDownItemOptionType[];
  onSelect?(item: DropDownItemOptionType): void;
  onClose?(): void;
}

type StoreType = {
  isShowList: boolean;
  input: string;
  selectedOption: DropDownItemOptionType | null;
  dimensions: MeasureDimensionsType | null;
  isFocused: boolean;
  options: DropDownItemOptionType[];
  setIsFocused(value: boolean): void;
  setIsShowList(value: boolean): void;
  toggleList(): void;
  setDimensions(dimensions: MeasureDimensionsType): void;
  selectOption(option: DropDownItemOptionType): void;
  onChangeInputText(value: string): void;
  get searchedOptions(): DropDownItemOptionType[];
};

/**
 * NOTE: this component was tested only for Web platform
 */
export const DropDownList = observer(
  ({
    containerRef,
    options,
    onSelect,
    onClose,
    ...props
  }: IDropDownListProps) => {
    const inputRef = useRef<TextInputRefType>(null);
    const innerInputRef = useRef<TextInputRefType>(null);
    const measureLayout = useMeasureLayout(inputRef, containerRef);
    const fuse = useVar(new Fuse(options, { keys: ['title'] }));
    const store = useLocalObservable<StoreType>(() => ({
      isShowList: false,
      input: '',
      selectedOption: null,
      dimensions: null,
      isFocused: false,
      options,

      setIsFocused(value: boolean) {
        this.isFocused = value;
      },

      setIsShowList(value: boolean) {
        this.isShowList = value;
      },

      toggleList() {
        this.isShowList = !this.isShowList;
      },

      setDimensions(dimensions: MeasureDimensionsType) {
        this.dimensions = dimensions;
      },

      selectOption(option: DropDownItemOptionType) {
        this.isShowList = false;
        this.selectedOption = option;
        this.input = '';
      },

      onChangeInputText(value: string) {
        this.input = value;
      },

      get searchedOptions() {
        return this.input
          ? fuse.search(this.input).map((i) => i.item)
          : this.options;
      },
    }));

    useEffect(() => {
      if (IS_WEB) {
        // Listen for clicks outside the dropdown
        document.addEventListener('click', () => {
          if (store.isShowList) {
            store.setIsShowList(false);
            onCloseHandler();
          }
        });
      }
    }, []);

    function measure() {
      measureLayout((d) => store.setDimensions(d));
    }

    function onCloseHandler() {
      if (onClose) {
        onClose();
      }
    }

    function onFocusHandler() {
      store.setIsFocused(true);
      store.setIsShowList(true);
      measure();
    }

    function onBlurHandler() {
      store.setIsFocused(false);
    }

    function onPressInputIcon() {
      if (!store.isShowList) {
        measure();
      } else {
        onCloseHandler();
      }
      store.toggleList();
    }

    function onPressItem(item: DropDownItemOptionType) {
      store.selectOption(item);
      onCloseHandler();
      if (onSelect) {
        onSelect(item);
      }
    }

    function focusInput() {
      store.setIsShowList(true);
      innerInputRef.current?.focus();
    }

    const arrowIconName = store.isShowList
      ? 'small-arrow-up-light'
      : 'small-arrow-down-light';

    return (
      <Fragment>
        <Touchable
          type={TouchableTypeEnum.WithoutFeedback}
          onPress={focusInput}
        >
          <PlaceholderInputContainer
            pointerEvents="none"
            isShowList={store.isShowList}
          >
            <TextInput
              pointerEvents="none"
              inputRef={innerInputRef}
              value={store.input}
              onFocus={onFocusHandler}
              onBlur={onBlurHandler}
              iconRight={arrowIconName}
              onChangeText={store.onChangeInputText}
              {...props}
            />
          </PlaceholderInputContainer>
          <TextInput
            pointerEvents="none"
            inputRef={inputRef}
            readOnly
            value={store.selectedOption?.title ?? ''}
            iconRight={{
              name: arrowIconName,
              onPress: onPressInputIcon,
            }}
            onChangeText={store.onChangeInputText}
            {...props}
          />
        </Touchable>
        {store.dimensions && store.isShowList && (
          <DropDownContainer top={store.dimensions.top}>
            <FlatList
              data={store.searchedOptions}
              keyExtractor={(_, index) => index.toString()}
              contentContainerSx={listContentContainerStyle}
              ItemSeparatorComponent={Line}
              renderItem={({ item }) => (
                <DropDownItemContainer>
                  <DropDownItem
                    option={item}
                    isActive={
                      store.selectedOption?.value === item.value
                    }
                    onPress={onPressItem}
                  />
                </DropDownItemContainer>
              )}
            />
          </DropDownContainer>
        )}
      </Fragment>
    );
  },
);
